import {
  Alert,
  Box,
  Icon,
  Text,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { FormikProvider } from 'formik'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EvaluationFormEditor } from 'modules/ats/components/EvaluationForm/components/EvaluationFormEditor'
import {
  buildEmptyQuestion,
  useEvaluationFormForm,
} from 'modules/ats/components/EvaluationForm/useEvaluationFormForm'

import { useCreateEvaluationFormTemplate } from './useCreateEvaluationFormTemplate'

interface Props {
  onClose: () => void
  evaluationFormId?: string
  duplicate?: boolean
}

export const CreateEvaluationFormTemplateSidepanel = ({
  onClose,
  evaluationFormId,
  duplicate,
}: Props) => {
  const session = useCurrentSession()
  const { openDialog } = useConfirmationDialog()
  const { create } = useCreateEvaluationFormTemplate()

  const { form, hasInvalidQuestions } = useEvaluationFormForm({
    defaultValues: {
      name: '',
      questions: [buildEmptyQuestion()],
      duplicate: duplicate ?? false,
    },
    async onSubmit(data, { setErrors }) {
      const result = await create({
        companyId: session.company.id,
        name: data.name,
        questions: data.questions.map((question) => ({
          id: question.id,
          description: question.description,
          text: question.text,
        })),
      })
      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.evaluation_forms.new.success_message'),
        })
        onClose()
      }

      return handled
    },
  })

  const onCancelClicked = () => {
    if (form.dirty) {
      openDialog({
        illustration: 'alert',
        title: i18n.t('ats.evaluation_forms.form.unsaved_changes.title'),
        content: i18n.t('ats.evaluation_forms.form.unsaved_changes.description'),
        confirmLabel: i18n.t('ats.evaluation_forms.form.unsaved_changes.cancel_and_delete'),
        cancelLabel: i18n.t('ats.evaluation_forms.form.unsaved_changes.continue'),
        onConfirm: onClose,
      })
    } else {
      onClose()
    }
  }

  return (
    <FormikProvider value={form}>
      <Sidepanel
        open
        width="wide"
        title={i18n.t('ats.evaluation_forms.new.title')}
        footer={
          <Box flexDirection="row" justifyContent="spaceBetween" width="full" alignItems="center">
            {form.dirty ? (
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Icon.Medium icon="alert-circle-line" color="grey800" />
                <Text.Normal size="200">{i18n.t('ats.evaluation_forms.form.warning')}</Text.Normal>
              </Box>
            ) : (
              <></>
            )}

            <Box flexDirection="row" gap="s8" alignSelf="flexEnd">
              <F0Button
                onClick={onCancelClicked}
                variant="outline"
                label={i18n.t('common.cancel')}
              />
              <F0Button
                onClick={() => form.handleSubmit()}
                disabled={form.isSubmitting}
                label={i18n.t('common.save')}
              />
            </Box>
          </Box>
        }
        onClose={onClose}
        closeLabel={i18n.t('common.close')}
      >
        <Box gap="s16">
          <Alert>{i18n.t('ats.evaluation_forms.new.info')}</Alert>
          <EvaluationFormEditor
            evaluationFormId={evaluationFormId}
            hasInvalidQuestions={hasInvalidQuestions}
          />
        </Box>
      </Sidepanel>
    </FormikProvider>
  )
}
